import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowLeft, MailCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ForgotPasswordPage = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitted || loading) return;
    setError(null);
    if (!EMAIL_RE.test(email.trim())) {
      setError(t('auth.forgotPassword.invalidEmail'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          frontend_base_url: window.location.origin,
        }),
      });
      // Always show the same neutral state regardless of response
      // (account-enumeration protection). Only surface a message for
      // network/genuine service errors.
      if (!res.ok && res.status >= 500) {
        setError(t('auth.forgotPassword.retryLater'));
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      setError(t('auth.forgotPassword.retryLater'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-slate-50">
      <Card className="w-full max-w-md" data-testid="forgot-password-card">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl" data-testid="forgot-password-title">
            {t('auth.forgotPassword.title')}
          </CardTitle>
          <CardDescription data-testid="forgot-password-subtitle">
            {t('auth.forgotPassword.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="text-center space-y-4" role="status" aria-live="polite"
                 data-testid="forgot-password-success">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 flex items-center justify-center">
                <MailCheck className="w-6 h-6 text-emerald-600" />
              </div>
              <p className="text-sm text-slate-700">
                {t('auth.forgotPassword.neutralMessage')}
              </p>
              <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-brand hover:underline"
                    data-testid="forgot-password-back-to-login">
                <ArrowLeft className="w-4 h-4" /> {t('auth.forgotPassword.backToLogin')}
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <div className="space-y-2">
                <Label htmlFor="fp-email">{t('auth.email')}</Label>
                <Input
                  id="fp-email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={!!error}
                  aria-describedby={error ? 'fp-email-error' : undefined}
                  data-testid="forgot-password-email-input"
                  required
                />
                {error && (
                  <p id="fp-email-error" role="alert"
                     className="text-sm text-rose-600" data-testid="forgot-password-error">
                    {error}
                  </p>
                )}
              </div>
              <Button type="submit" className="w-full" disabled={loading || !email}
                      data-testid="forgot-password-submit">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('auth.forgotPassword.submit')}
              </Button>
              <div className="text-center">
                <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"
                      data-testid="forgot-password-back-link">
                  <ArrowLeft className="w-4 h-4" /> {t('auth.forgotPassword.backToLogin')}
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
